/**
 * AllEventsPage - Events Discovery
 * 
 * Shows all events from user's clubs with filtering and date grouping.
 * Rebuilt to match Figma Nightfall Kit design with EventCard components.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7B/7C
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { TopBar, EventCard, EmptyState, EventCardSkeleton } from '../components';
import { useAllEvents } from '../hooks/useEvents';
import { createEventsMobileMenu } from '../components/MobileContextMenu';
import { Plus } from 'lucide-react';
import type { Event } from '../components/EventCard';

// Group events by date
function groupEventsByDate(events: Event[]): Map<string, Event[]> {
  const groups = new Map<string, Event[]>();
  
  events.forEach(event => {
    const date = event.date; // Already formatted as "Mon, Dec 2"
    if (!groups.has(date)) {
      groups.set(date, []);
    }
    groups.get(date)!.push(event);
  });
  
  return groups;
}

export default function AllEventsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const { data: events, isLoading, error } = useAllEvents();

  // Create mobile menu configuration
  const eventsMobileMenu = createEventsMobileMenu(navigate, logout);

  // Transform API events to EventCard format and filter
  const displayEvents = useMemo(() => {
    if (!events) return [];
    
    return events
      .filter(event => {
        const eventDate = new Date(event.starts_at * 1000);
        const now = new Date();
        
        if (filter === 'upcoming') return eventDate >= now;
        if (filter === 'past') return eventDate < now;
        return true;
      })
      .map(event => ({
        id: event.id,
        title: event.name || event.title,
        date: new Date(event.starts_at * 1000).toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        }),
        location: event.location_name || 'Location TBD',
        attendees: event.attendee_count || event.rsvp_count || 0,
        imageUrl: event.hero_image_url || '' // Backend COALESCE handles event → club fallback
      }));
  }, [events, filter]);

  // Group events by date for separators
  const groupedEvents = useMemo(() => groupEventsByDate(displayEvents), [displayEvents]);

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar title="Events" mobileMenu={eventsMobileMenu} />
      
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          {/* Filter Tabs */}
          <div className="flex gap-2 border-b border-oro-border -mx-4 px-4">
            {(['upcoming', 'all', 'past'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 font-medium capitalize transition-colors ${
                  filter === tab
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <EventCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="py-12">
              <EmptyState
                icon="⚠️"
                title="Failed to load events"
                message={error instanceof Error ? error.message : 'Something went wrong'}
                actionLabel="Try Again"
                onAction={() => window.location.reload()}
              />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && displayEvents.length === 0 && (
            <div className="py-12">
              <EmptyState
                icon="📅"
                title={filter === 'past' ? 'No past events' : 'No upcoming events'}
                message={
                  filter === 'past'
                    ? 'Events you\'ve attended will appear here'
                    : 'Create an event or join a club to see upcoming events'
                }
                actionLabel={filter !== 'past' ? 'Create Event' : undefined}
                onAction={filter !== 'past' ? () => navigate('/events/create') : undefined}
              />
            </div>
          )}

          {/* Event List with Date Separators */}
          {!isLoading && !error && displayEvents.length > 0 && (
            <div className="space-y-6">
              {Array.from(groupedEvents.entries()).map(([date, dateEvents]) => (
                <div key={date} className="space-y-3">
                  {/* Date Separator */}
                  <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">
                    {date}
                  </h2>
                  
                  {/* Events for this date */}
                  <div className="space-y-3">
                    {dateEvents.map(event => (
                      <EventCard
                        key={event.id}
                        event={event}
                        onClick={() => navigate(`/event/${event.id}`)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Floating Create Button */}
      <button
        onClick={() => navigate('/events/create')}
        className="fixed bottom-24 left-4 w-11 h-11 bg-accent hover:bg-accent/90 text-black rounded-2xl shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
        aria-label="Create event"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}